import { motion } from "framer-motion";
import { MessageCircle, Heart, Gift, Truck, CreditCard, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { WA_MAIN, INSTAGRAM_URL } from "@/lib/api";

const steps = [
  { icon: Gift, title: "Escolha", text: "Navegue pelo catálogo e encontre a cesta perfeita" },
  { icon: MessageCircle, title: "Peça no WhatsApp", text: "Toque em pedir e fale direto com a gente" },
  { icon: Heart, title: "Surpreenda", text: "Preparamos tudo à mão para o seu momento especial" },
];

export const HowItWorks = () => (
  <section id="como-funciona" className="bg-sand py-16 md:py-24" data-testid="how-it-works">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="text-center mb-12"
      >
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand mb-3">Simples assim</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-espresso">
          Como funciona
        </h2>
      </motion.div>
      <div className="grid sm:grid-cols-3 gap-6 md:gap-10">
        {steps.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="bg-white rounded-2xl border border-wood/10 p-8 text-center shadow-[0_4px_20px_rgba(74,53,37,0.05)]"
          >
            <span className="inline-flex w-14 h-14 rounded-full bg-brand/10 items-center justify-center mb-4">
              <s.icon className="w-6 h-6 text-brand" />
            </span>
            <h3 className="font-display text-xl font-semibold text-espresso">{s.title}</h3>
            <p className="text-sm text-wood/60 mt-2 leading-relaxed">{s.text}</p>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mt-10 max-w-2xl mx-auto bg-white rounded-2xl border border-brand/20 shadow-[0_4px_20px_rgba(74,53,37,0.06)] px-6 py-5 flex items-center gap-4"
        data-testid="delivery-info"
      >
        <span className="shrink-0 w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center">
          <Truck className="w-6 h-6 text-brand" />
        </span>
        <p className="text-sm sm:text-base text-wood/80 leading-relaxed">
          <strong className="text-espresso">Entregamos em Sapucaia do Sul e região!</strong>{" "}
          O valor é calculado à parte, conforme o endereço de entrega.
        </p>
      </motion.div>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="bg-[#EFE7DA] text-espresso" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid md:grid-cols-3 gap-10 items-start">
        <div>
          <img src="/logo.png" alt="Cestou Recordações" className="h-24 w-auto mb-4" data-testid="footer-logo" />
          <p className="font-script text-2xl text-espresso">Cestas e presentes para momentos especiais!</p>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-espresso/70 hover:text-[#A67C1A] transition-colors"
          >
            <Instagram className="w-4 h-4" />
            Siga a gente: @cestourecordacoes
          </a>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-espresso/50 mb-4">Pedidos</h4>
          <a
            href={WA_MAIN}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp-button"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white font-semibold px-6 py-3 rounded-full transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            (51) 98997-5814
          </a>
          <p className="text-sm text-espresso/60 mt-4 flex items-center gap-2">
            <Truck className="w-4 h-4" /> Sapucaia do Sul · RS
          </p>
          <p className="text-sm text-espresso/60 mt-2 flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Pagamento: PIX e cartão
          </p>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-espresso/50 mb-4">Catálogo</h4>
          <a href="/#catalogo" className="text-sm text-espresso/70 hover:text-[#A67C1A] transition-colors" data-testid="footer-catalog-link">
            Ver todos os produtos
          </a>
        </div>
      </div>
      <div className="border-t border-wood/10 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-espresso/40">© 2026 Cestou Recordações. Todos os direitos reservados.</p>
        <Link
          to="/admin"
          data-testid="admin-link"
          className="text-xs text-espresso/40 hover:text-[#A67C1A] transition-colors underline underline-offset-4"
        >
          Área do Administrador
        </Link>
      </div>
    </div>
  </footer>
);

export const WhatsAppFloat = () => (
  <motion.a
    href={WA_MAIN}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 1.4, type: "spring", stiffness: 200 }}
    data-testid="whatsapp-float-button"
    className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] shadow-xl shadow-[#25D366]/40 flex items-center justify-center hover:scale-110 transition-transform"
    aria-label="Pedir pelo WhatsApp"
  >
    <MessageCircle className="w-7 h-7 text-white" />
  </motion.a>
);
